import { Auth } from "@aws-amplify/auth";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUser } from "context/AuthContext";
import { useFormFields } from "libs/fields";
import { onError } from "libs/error";
import { generate } from "random-words";
import Modal from "components/Modal";
import HttpRequest from "api/HttpRequest";
import { ClickButton, SubmitButton, TextInput } from "components/FormElements";
import Header from "components/Header";
import SubHeader from "components/SubHeader";
import Footer from "components/Footer";

export default function Blockchain() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { user, setUser } = useUser();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [address, setAddress] = useState<any>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<JSX.Element>(<span></span>);

  const [fields, handleFieldChange] = useFormFields({
    seed: generate({ exactly: 6, maxLength: 10 }).join(" ")
  });

  function validateForm() {
    return fields.seed.length >= 32;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    setIsLoading(true);

    try {
      const blockchain = await HttpRequest.generateBookchainAddress({
        salt: fields.seed,
        email: user.attributes.email
      });

      setAddress(blockchain.address_hash);

      await Auth.updateUserAttributes(user, {
        "custom:blockchain_address": blockchain.address_hash
      });

      const refreshUser = await Auth.currentAuthenticatedUser({
        bypassCache: true
      });
      setUser(refreshUser);

      setIsLoading(false);
    } catch (error: unknown) {
      const e = error as Error;
      const errorHandler = onError(e);
      setErrorMessage(errorHandler);
      setIsLoading(false);
    }
  }

  const completeSignup = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowModal(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <SubHeader heading={t("local.blockchain.title.main").toString()} />
      <main className="flex-grow bg-ct-lightgrey px-4 md:px-0">
        <h2 className="text-3xl text-black text-center pt-20 mb-2.5">{t("local.blockchain.title.main")}</h2>
        <p className="text-base text-black text-center whitespace-pre-line	mb-6 max-w-2xl mx-auto">{t("local.blockchain.subtitle.main")}</p>
        <form className="container mx-auto max-w-md flex flex-col justify-center space-y-4 pb-20" onSubmit={handleSubmit}>
          <TextInput
            handleChange={handleFieldChange}
            name="seed"
            value={address ? address : fields.seed}
            label={address ? t("local.account.label.blockchain") : t("local.blockchain.label.salt")}
            readonly={true}
          />
          {address ? (
            <ClickButton handleClick={completeSignup} disabled={isLoading} loading={isLoading} label={t("global.button.completesignup")} />
          ) : (
            <SubmitButton disabled={!validateForm() || isLoading} loading={isLoading} label={t("global.button.generate")} />
          )}
          {errorMessage}
        </form>
      </main>
      {showModal && (
        <Modal
          title={t("local.blockchain.title.confirmation").toString()}
          subtitle={t("local.blockchain.subtitle.confirmation").toString()}
          handleClick={() => navigate("/")}
          clickLabel={t("global.button.continue")}
        />
      )}
      <Footer />
    </div>
  );
}
